/**
 * Générateur — Appliquer un pourcentage à une grandeur ou un nombre
 * Attendu ID : 636 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 3 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 8 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER19 §6) :
 *
 *   1. `pourcentage_comme_fraction`  → QCM (5 %), Relier (4 paires %).
 *   2. `appliquer_pourcentage`       → QCM, Saisie, Texte à trous.
 *   3. `probleme_pourcentage`        → QCM, Saisie, Texte à trous.
 *
 * Règles dures de la fiche (§3, §4, §6) :
 *   - Pourcentages courants : {10 %, 20 %, 25 %, 50 %, 75 %}
 *   - Couples (a %, c) : résultat = a/100 × c ∈ ℤ ou décimal ≤ 1 chiffre
 *   - 6 contextes concrets (chocolat, soldes, effectifs, clubs, récolte, votes)
 *   - Distracteurs : a × c, c / a, a seul, c − résultat, mauvaise fraction
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier,
} from '../../../utils';
import { gcd, simplify, fmtFrac, fmtKatex, fmtItem } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 636;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FRAC_QCM      = 'Reconnaître la fraction simplifiée d\'un pourcentage courant';
const L_FRAC_RELIER  = 'Relier pourcentages courants et fractions';
const L_APPLY_QCM    = 'Calculer un pourcentage d\'un nombre';
const L_APPLY_SAISIE = 'Calculer un pourcentage d\'un nombre (saisie)';
const L_APPLY_TROU   = 'Calculer un pourcentage d\'un nombre (étapes guidées)';
const L_PROB_QCM     = 'Résoudre un problème avec pourcentage';
const L_PROB_SAISIE  = 'Résoudre un problème avec pourcentage (saisie)';
const L_PROB_TROU    = 'Résoudre un problème avec pourcentage (étapes guidées)';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Formate un nombre décimal français (virgule). */
const fmtNum = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

// ═══════════════════════════════════════════════════════════
// POOLS
// ═══════════════════════════════════════════════════════════

/** Pourcentages courants avec fraction simplifiée et label. */
const COMMON_PCT = [
    { pct: 10, num: 1, den: 10, label: 'un dixième' },
    { pct: 20, num: 1, den: 5, label: 'un cinquième' },
    { pct: 25, num: 1, den: 4, label: 'un quart' },
    { pct: 50, num: 1, den: 2, label: 'la moitié' },
    { pct: 75, num: 3, den: 4, label: 'les trois quarts' },
    // Extensions : pourcentages usuels avec dénominateurs familiers (/2, /4, /5, /10, /20)
    { pct: 5,  num: 1, den: 20, label: 'un vingtième' },
    { pct: 30, num: 3, den: 10, label: 'trois dixièmes' },
    { pct: 40, num: 2, den: 5,  label: 'deux cinquièmes' },
    { pct: 60, num: 3, den: 5,  label: 'trois cinquièmes' },
    { pct: 70, num: 7, den: 10, label: 'sept dixièmes' },
    { pct: 80, num: 4, den: 5,  label: 'quatre cinquièmes' },
    { pct: 90, num: 9, den: 10, label: 'neuf dixièmes' },
];

// APPLY_COUPLES supprimé : remplacé par genAppliquerCouple() qui garantit result entier
// via la contrainte c multiple de (100 / gcd(pct, 100)).

/** Contextes concrets pour les problèmes. */
const CTX_PROBLEME = [
    {
        id: 'aliment',
        template: (pct, c) => `Une tablette de chocolat pèse ${c} g. Elle contient ${pct} % de cacao.`,
        question: 'Quelle masse de cacao contient-elle ?',
        questionComplement: 'Quelle masse de la tablette n\'est PAS du cacao ?',
        suffixe: 'g',
    },
    {
        id: 'soldes',
        template: (pct, c) => `Un article coûte ${c} €. Il est soldé à ${pct} % de réduction.`,
        question: 'Quel est le montant de la réduction ?',
        questionComplement: 'Quel est le prix soldé ?',
        suffixe: '€',
    },
    {
        id: 'ecole',
        template: (pct, c) => `Un collège compte ${c} élèves. ${pct} % sont demi-pensionnaires.`,
        question: 'Combien d\'élèves sont demi-pensionnaires ?',
        questionComplement: 'Combien d\'élèves sont externes ?',
        suffixe: 'élèves',
    },
    {
        id: 'club',
        template: (pct, c) => `Un club sportif compte ${c} membres. ${pct} % pratiquent le tennis.`,
        question: 'Combien de membres pratiquent le tennis ?',
        questionComplement: 'Combien de membres ne pratiquent PAS le tennis ?',
        suffixe: 'membres',
    },
    {
        id: 'recolte',
        template: (pct, c) => `Un agriculteur a récolté ${c} kg de pommes. ${pct} % sont abîmées.`,
        question: 'Combien de kg sont abîmés ?',
        questionComplement: 'Combien de kg sont en bon état ?',
        suffixe: 'kg',
    },
    {
        id: 'vote',
        template: (pct, c) => `Lors d'une élection, ${c} personnes ont voté. Le candidat a obtenu ${pct} % des voix.`,
        question: 'Combien de voix a-t-il obtenues ?',
        questionComplement: 'Combien de personnes n\'ont PAS voté pour lui ?',
        suffixe: 'voix',
    },
];

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — pourcentage_comme_fraction (QCM, Relier)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : Quelle fraction simplifiée = ce pourcentage courant ?
 */
const genFractionQcm = () => {
    const item = randomChoice(COMMON_PCT);
    const bonne = fmtItem(item.num, item.den);

    // Distracteurs
    const distractors = new Set();

    // D1 : fraction renversée (50% → 1/50 au lieu de 1/2)
    distractors.add(`$\\dfrac{1}{${item.pct}}$`);

    // D2 : fraction non simplifiée (25% → 25/100 au lieu de 1/4)
    distractors.add(`$\\dfrac{${item.pct}}{100}$`);

    // D3 : autre pourcentage courant (voisin ou aléatoire)
    const otherPct = randomChoice(COMMON_PCT.filter(p => p.pct !== item.pct));
    distractors.add(fmtItem(otherPct.num, otherPct.den));

    // D4 : mauvais numérateur
    const wrongNum = item.num === 1 ? 2 : item.num + 1;
    distractors.add(fmtItem(wrongNum, item.den));

    // Déduplique
    const dArr = [...distractors].filter(d => d !== bonne).slice(0, 3);
    while (dArr.length < 3) {
        const f = fmtItem(randInt(1, 5), randInt(2, 10));
        if (f !== bonne && !dArr.includes(f)) dArr.push(f);
    }

    const texte = `<p>Quelle fraction simplifiée correspond à ${item.pct} % ?</p>`;
    const choix = [bonne, ...dArr];
    const payload = makeQcm(choix, bonne, texte, opts(L_FRAC_QCM));
    payload._meta.generatorKey = 'pourcentage_comme_fraction';
    return payload;
};

/**
 * Relier : 4 pourcentages courants ↔ fractions simplifiées.
 */
const genFractionRelier = () => {
    const selected = shuffle(COMMON_PCT).slice(0, 4);
    const pairs = selected.map(item => ({
        source: `${item.pct} %`,
        target: fmtItem(item.num, item.den),
    }));

    const texte = `<p>Relie chaque pourcentage à sa fraction simplifiée.</p>`;
    const payload = makeRelier(pairs, texte, opts(L_FRAC_RELIER));
    payload._meta.generatorKey = 'pourcentage_comme_fraction';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — appliquer_pourcentage (QCM, Saisie, Texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * Génère dynamiquement un couple (pct, c, result) avec result entier garanti.
 * - pct ∈ liste de pourcentages usuels
 * - c multiple de (100 / gcd(pct, 100)) → (pct × c) divisible par 100
 * - result = (pct × c) / 100 entier ∈ [1, 200]
 */
const genAppliquerCouple = () => {
    const pctChoices = [5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 80];
    for (let i = 0; i < 30; i++) {
        const pct = randomChoice(pctChoices);
        const step = 100 / gcd(pct, 100);        // c doit être multiple de step
        const k = randInt(1, Math.floor(300 / step));
        const c = k * step;
        if (c < 10) continue;                     // c minimum 10 (lisible)
        const result = (pct * c) / 100;           // toujours entier par construction
        if (result < 1 || result > 200) continue; // résultat raisonnable
        return { pct, c, result };
    }
    // Fallback ultime (quasi jamais atteint) : cas canonique
    return { pct: 10, c: 60, result: 6 };
};

/**
 * QCM : Calcule a % de c.
 */
const genAppliquerQcm = () => {
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;
    const bonne = fmtNum(result);

    // Distracteurs
    const distractors = new Set();

    // D1 : a × c (oubli /100)
    distractors.add(String(pct * c));

    // D2 : c / a
    const divRes = Math.round((c / pct) * 10) / 10;
    distractors.add(fmtNum(divRes));

    // D3 : a seul
    distractors.add(String(pct));

    // D4 : c − result (complément)
    distractors.add(fmtNum(c - result));

    const dArr = [...distractors].filter(d => d !== bonne).slice(0, 3);
    while (dArr.length < 3) {
        const fallback = String(randInt(1, 200));
        if (fallback !== bonne && !dArr.includes(fallback)) dArr.push(fallback);
    }

    const texte = `<p>Calcule ${pct} % de ${c}.</p>`;
    const choix = [bonne, ...dArr];
    const payload = makeQcm(choix, bonne, texte, opts(L_APPLY_QCM));
    payload._meta.generatorKey = 'appliquer_pourcentage';
    return payload;
};

/**
 * Saisie : Calcule a % de c.
 */
const genAppliquerSaisie = () => {
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;
    const bonne = fmtNum(result);

    const texte = `<p>Calcule ${pct} % de ${c}.</p>`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_APPLY_SAISIE), strict: false });
    payload._meta.generatorKey = 'appliquer_pourcentage';
    return payload;
};

/**
 * Texte à trous : "a % de c = a/100 × c = {{result}}"
 */
const genAppliquerTrou = () => {
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;

    const texte = `<p>Complète le calcul : ${pct} % de ${c}.</p>`;
    const phrase = `$${pct}\\,\\%\\text{ de }${c} = \\dfrac{${pct}}{100} \\times ${c} = {{${fmtNum(result)}}}$`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_APPLY_TROU), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'appliquer_pourcentage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — probleme_pourcentage (QCM, Saisie, Texte à trous)
// ═══════════════════════════════════════════════════════════

/**
 * QCM : Problème contextualisé avec pourcentage.
 * Environ 50 % des problèmes portent sur le complément.
 */
const genProblemeQcm = () => {
    const ctx = randomChoice(CTX_PROBLEME);
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;

    // Décide si on demande le résultat direct ou le complément
    const isComplement = randInt(0, 1) === 1;
    const bonne = isComplement ? fmtNum(c - result) : fmtNum(result);
    const question = isComplement ? ctx.questionComplement : ctx.question;

    const enonce = ctx.template(pct, c);
    const texte = `<p>${enonce}</p><p>${question}</p>`;

    // Distracteurs
    const distractors = new Set();
    if (isComplement) {
        distractors.add(fmtNum(result));  // La bonne réponse directe au lieu du complément
    } else {
        distractors.add(fmtNum(c - result));  // Le complément au lieu de la réponse directe
    }
    distractors.add(String(pct * c));     // Oubli /100
    distractors.add(String(pct));         // Confusion pourcentage/résultat
    const nearby = String(result + 1 < c - result ? result + 1 : result - 1);
    if (nearby !== bonne) distractors.add(nearby);

    const dArr = [...distractors].filter(d => d !== bonne).slice(0, 3);
    while (dArr.length < 3) {
        const fallback = String(randInt(1, 300));
        if (fallback !== bonne && !dArr.includes(fallback)) dArr.push(fallback);
    }

    const choix = [bonne, ...dArr];
    const payload = makeQcm(choix, bonne, texte, opts(L_PROB_QCM));
    payload._meta.generatorKey = 'probleme_pourcentage';
    return payload;
};

/**
 * Saisie : Problème contextualisé avec réponse numérique + suffixe.
 */
const genProblemeSaisie = () => {
    const ctx = randomChoice(CTX_PROBLEME);
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;

    const isComplement = randInt(0, 1) === 1;
    const bonne = fmtNum(isComplement ? c - result : result);
    const question = isComplement ? ctx.questionComplement : ctx.question;

    const enonce = ctx.template(pct, c);
    const texte = `<p>${enonce}</p><p>${question}</p>`;
    const payload = makeSaisie(bonne, texte, { ...opts(L_PROB_SAISIE), strict: false, suffixe: ctx.suffixe });
    payload._meta.generatorKey = 'probleme_pourcentage';
    return payload;
};

/**
 * Texte à trous : "a % de c = a/100 × c = {{result}} unité. Il reste donc c − {{result}} = {{reste}} unité."
 * Environ 50 % des fois, on ajoute aussi la question du complément.
 */
const genProblemeTexteTrou = () => {
    const ctx = randomChoice(CTX_PROBLEME);
    const couple = genAppliquerCouple();
    const { pct, c, result } = couple;

    const includeComplement = randInt(0, 1) === 1;

    const enonce = ctx.template(pct, c);
    const texte = `<p>${enonce} ${ctx.question}</p>`;

    let phrase = `$${pct}\\,\\%\\text{ de }${c} = \\dfrac{${pct}}{100} \\times ${c} = {{${fmtNum(result)}}}\\text{ ${ctx.suffixe}}$`;

    if (includeComplement) {
        const remaining = c - result;
        phrase += `\n$${c} - {{${fmtNum(result)}}} = {{${fmtNum(remaining)}}}\\text{ ${ctx.suffixe} qui ne sont pas concernés.}$`;
    }

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PROB_TROU), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'probleme_pourcentage';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    pourcentage_comme_fraction_qcm:     genFractionQcm,
    pourcentage_comme_fraction_relier:  genFractionRelier,
    appliquer_pourcentage_qcm:          genAppliquerQcm,
    appliquer_pourcentage_saisie:       genAppliquerSaisie,
    appliquer_pourcentage_texte_trou:   genAppliquerTrou,
    probleme_pourcentage_qcm:           genProblemeQcm,
    probleme_pourcentage_saisie:        genProblemeSaisie,
    probleme_pourcentage_texte_trou:    genProblemeTexteTrou,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        id: 'pourcentage_comme_fraction',
        label: 'Reconnaître la fraction d\'un pourcentage',
        formatsList: buildFormats('pourcentage_comme_fraction'),
    },
    {
        id: 'appliquer_pourcentage',
        label: 'Appliquer un pourcentage à un nombre',
        formatsList: buildFormats('appliquer_pourcentage'),
    },
    {
        id: 'probleme_pourcentage',
        label: 'Résoudre un problème avec pourcentage',
        formatsList: buildFormats('probleme_pourcentage'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    gcd, simplify, fmtFrac, fmtKatex, fmtItem, fmtNum,
    COMMON_PCT, CTX_PROBLEME,
    genFractionQcm, genFractionRelier,
    genAppliquerQcm, genAppliquerSaisie, genAppliquerTrou,
    genProblemeQcm, genProblemeSaisie, genProblemeTexteTrou,
};
